import React, { useCallback, useMemo } from "react";

import { Stack } from "@mui/material";
import { useNavigate } from "react-router-dom-v5-compat";

import useAccountHoldingsV2 from "@fartherfinance/frontend/api/Accounts/hooks/useAccountHoldingsV2";
import useGetExcludedAssets from "@fartherfinance/frontend/api/Investment/hooks/useGetExcludedAssets";
import useGetPortfolioPlanAssignments from "@fartherfinance/frontend/api/Investment/hooks/usePortfolioPlanAssignment";
import {
  ClientId,
  PortfolioPlanAssignmentId,
  PortfolioPlanId,
} from "@fartherfinance/frontend/api/Types";

import Button from "@src/multiCustodian/components/MUI/Button/Button";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import { filterHoldings } from "@src/multiCustodian/pages/Client/:clientId/Investments/PortfoliosV2/Accounts/DoNotSell/helpers";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import TooltipBasic from "@src/sharedComponents/Tooltip/TooltipBasic";
import Text from "@src/sharedComponents/Typography/Text";
import { pluralize } from "@src/utils/pluralize";

import styles from "./TradingGroupsTable.module.css";

interface Props {
  clientId: ClientId;
  portfolioPlanId: PortfolioPlanId;
  assignmentId: PortfolioPlanAssignmentId;
}

const DoNotSellButtonV2: React.FC<Props> = ({
  clientId,
  portfolioPlanId,
  assignmentId,
}) => {
  const navigate = useNavigate();

  const config = useRequestAuth();

  const assignment = useGetPortfolioPlanAssignments(
    {
      abstractPortfolioId: portfolioPlanId,
      portfolioPlanAssignmentId: assignmentId,
    },
    config
  );

  const accounts = assignment.data?.accounts;

  const accountHoldings = useAccountHoldingsV2(
    clientId,
    accounts
      ? {
          virtualAccountIds: accounts,
          $expand: ["securityDetails"],
        }
      : null,
    config
  );

  const excludedAssets = useGetExcludedAssets(
    portfolioPlanId,
    assignmentId,
    config
  );

  const navigateToDoNotSell = useCallback(() => {
    navigate(
      `/Client/${clientId}/Investments/PortfoliosV2/${portfolioPlanId}/Assignments/${assignmentId}/Do-not-sell`
    );
  }, [assignmentId, clientId, navigate, portfolioPlanId]);

  const title = useMemo(() => {
    if (
      excludedAssets.data !== undefined &&
      accountHoldings.data !== undefined &&
      accountHoldings.data.records !== undefined
    ) {
      // Use the shared helper function to filter holdings consistently
      const uniqueHoldings = filterHoldings(accountHoldings.data.records);

      const uniqueCount = uniqueHoldings.length;
      const excludedCount = excludedAssets.data.length;

      return `${excludedCount}/${uniqueCount} ${pluralize(
        uniqueCount,
        "security"
      )}`;
    }
    return "0/0 securities";
  }, [accountHoldings.data, excludedAssets.data]);

  const tooltipText = `Hold a security outside your portfolio strategy, without triggering a sale or disrupting overall portfolio management.

• When a security is marked as Do Not Sell, it will remain in the account, but it will not be rebalanced, traded, or used in tax-loss harvesting
• We will attempt to align the remaining holdings and cash in the account(s) with the selected model portfolio. If a security is marked as Do Not Sell, but also appears in the model portfolio, we will not buy any additional shares of that security. Instead, we'll scale up the remaining holdings in the model portfolio to account for it. Besides that adjustment, we will not make any risk-based adjustments based on the specific characteristics of the security/securities marked as Do Not Sell.`;

  return (
    <div className={styles.doNotSellButton}>
      <Stack direction={"row"} spacing={2}>
        <Stack direction={"row"} alignItems="center">
          <Text
            variant="body"
            color="textSubtle"
            className={styles.alignContentCenter}
          >
            Do not sell
          </Text>
          <TooltipBasic text={tooltipText} placement="top" />
        </Stack>
        <Text
          variant="body"
          color="textSubtle"
          className={styles.doNotSellDescription}
        >
          {accountHoldings.isLoading || excludedAssets.isLoading ? (
            <Stack direction={"row"} spacing={1}>
              <Skeleton width={20} />
              <Text variant="body" color="textSubtle">
                {"/"}
              </Text>
              <Skeleton width={20} />
            </Stack>
          ) : (
            title
          )}
        </Text>
      </Stack>

      <Button
        variant="outlined"
        onClick={navigateToDoNotSell}
        text={"View"}
        buttonType="secondary"
      />
    </div>
  );
};

export default DoNotSellButtonV2;
